import { useEffect, useMemo, useRef, useState } from "react";
import {
  ComposedChart, Scatter, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { DownloadSimple, ChartScatter, Function as FnIcon, Image as ImageIcon } from "@phosphor-icons/react";
import { getEeCorrelation, getAirTemperature, RS_EXPORT_URL } from "@/lib/api";
import { CHART } from "@/lib/chartTheme";
import { exportSvgAsPng } from "@/lib/exportPng";

function strength(r) {
  if (r == null) return "—";
  const a = Math.abs(r);
  const s = a < 0.3 ? "débil" : a < 0.6 ? "moderada" : a < 0.8 ? "fuerte" : "muy fuerte";
  const dir = r < 0 ? "negativa (inversa)" : "positiva (directa)";
  return `${s} y ${dir}`;
}

function CTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-[#ffffff] border border-[#e5e7eb] px-3 py-2 rounded-md text-xs mono">
      <div className="text-[#334155]">Año {p.year}</div>
      <div style={{ color: CHART.lst }}>Temp: {p.temp} °C</div>
      <div style={{ color: CHART.ndvi }}>NDVI: {p.ndvi}</div>
    </div>
  );
}

export default function CorrelationView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [airData, setAirData] = useState(null);
  const scatterRef = useRef(null);
  const seriesRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setData(null);
    getEeCorrelation().then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { getAirTemperature().then(setAirData).catch(() => {}); }, []);

  const rN = data?.temp_vs_ndvi;
  const isLandsat = true;
  const seeding = data && data.state && data.state !== "done";

  const scatterData = useMemo(() => {
    if (!data) return [];
    const s = data.temp_vs_ndvi;
    return [...data.series]
      .sort((a, b) => a.temp - b.temp)
      .map((d) => ({ ...d, reg: s.slope != null ? +(s.slope * d.temp + s.intercept).toFixed(4) : null }));
  }, [data]);

  return (
    <div className="flex-1 overflow-y-auto p-6 lg:p-8" data-testid="correlation-view">
      <div className="max-w-6xl mx-auto space-y-6 fade-up">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="plex text-2xl font-semibold tracking-tight">Correlación: variabilidad térmica ↔ vegetación agrícola</h2>
            <p className="text-sm text-[#334155] mt-1 max-w-2xl">
              Relación estadística entre la temperatura de superficie anual y el NDVI en las zonas
              agrícolas de Constanza, {data ? `${data.series[0]?.year}–${data.series[data.series.length - 1]?.year}` : "2000–2024"} (n = {data?.n ?? "…"} años).
              {" "}Fuente: <span className="text-[#1f2937]">Landsat 30 m (LST + NDVI, teledetección)</span>.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <a href={RS_EXPORT_URL} data-testid="export-csv"
              className="flex items-center gap-2 text-xs px-3 py-2 rounded-md bg-[#4a9b3c] hover:bg-[#3f8e33] text-white transition-colors">
              <DownloadSimple size={15} weight="bold" /> Exportar CSV
            </a>
            <button onClick={() => exportSvgAsPng(scatterRef.current, "correlacion_temp_ndvi.png")}
              data-testid="export-png"
              className="flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-[#e5e7eb] text-[#1f2937] hover:bg-[#eef7ed] transition-colors">
              <ImageIcon size={15} /> Figura PNG
            </button>
          </div>
        </div>

        {loading && <div className="text-sm mono text-[#334155] animate-pulse">Calculando correlación…</div>}
        {seeding && (
          <div className="text-xs mono text-amber-400 border border-amber-900/40 bg-amber-950/20 rounded-md px-3 py-2">
            Precargando serie Landsat en Earth Engine… {data.years_done}/{data.years_total} años.
          </div>
        )}

        {data && !loading && (
          <>
            <div className="grid gap-4 md:grid-cols-2">
              <RCard title="LST vs NDVI (Landsat 30 m)" r={rN?.r} testId="r-ndvi" />
              {airData?.lst_vs_air && (
                <RCard title="LST (superficie) vs Temp. del aire (INDOMET/ERA5)" r={airData.lst_vs_air.r} testId="r-lst-air" />
              )}
            </div>
            {airData?.lst_vs_air && (
              <div className="text-[12px] text-[#334155] border border-[#e5e7eb] bg-[#f8faf9] rounded-md px-3 py-2 -mt-2" data-testid="lst-air-note">
                La correlación entre la temperatura de <strong>superficie</strong> (LST satelital) y la del <strong>aire</strong> oficial
                (INDOMET/ERA5) es <span className="text-[#1f2937]">{strength(airData.lst_vs_air.r)}</span> (r = {airData.lst_vs_air.r}, n = {airData.lst_vs_air.n}).
                Son magnitudes físicas distintas; el análisis detallado está en la pestaña <span className="text-[#3f8e33]">Temp. del aire</span>.
              </div>
            )}

            <div className="grid lg:grid-cols-2 gap-6">
              <div className="border border-[#e5e7eb] bg-[#ffffff] rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <ChartScatter size={16} className="text-[#3f8e33]" />
                  <span className="plex text-sm font-medium">Dispersión + recta de regresión</span>
                </div>
                <div className="h-[320px]" ref={scatterRef} data-testid="scatter-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={scatterData} margin={{ top: 10, right: 16, left: -10, bottom: 16 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} />
                      <XAxis type="number" dataKey="temp" name="Temp" unit="°C" domain={["auto", "auto"]}
                        tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={{ stroke: "#e5e7eb" }} tickLine={false}
                        label={{ value: "Temperatura de superficie (°C)", position: "bottom", fill: "#64748b", fontSize: 11 }} />
                      <YAxis type="number" dataKey="ndvi" domain={["auto", "auto"]}
                        tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false}
                        label={{ value: "NDVI", angle: -90, position: "insideLeft", fill: "#64748b", fontSize: 11 }} />
                      <Tooltip content={<CTip />} />
                      <Line dataKey="reg" stroke={CHART.navy} strokeWidth={2} dot={false} isAnimationActive={false} name="Regresión" />
                      <Scatter dataKey="ndvi" fill={CHART.ndvi} isAnimationActive={false} name="Años" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-[11px] text-[#475569] mt-2">
                  Recta: NDVI = {rN?.slope} × Temp + {rN?.intercept}
                </p>
              </div>

              <div className="border border-[#e5e7eb] bg-[#ffffff] rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <FnIcon size={16} style={{ color: CHART.navy }} />
                  <span className="plex text-sm font-medium">Evolución anual (doble eje)</span>
                </div>
                <div className="h-[320px]" ref={seriesRef} data-testid="series-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={data.series} margin={{ top: 10, right: 8, left: -12, bottom: 16 }}>
                      <defs>
                        <linearGradient id="gCorrTemp" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={CHART.lst} stopOpacity={0.18} />
                          <stop offset="100%" stopColor={CHART.lst} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="gCorrNdvi" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={CHART.ndvi} stopOpacity={0.15} />
                          <stop offset="100%" stopColor={CHART.ndvi} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} />
                      <XAxis dataKey="year" tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={{ stroke: "#e5e7eb" }} tickLine={false} minTickGap={20} />
                      <YAxis yAxisId="t" orientation="left" tick={{ fill: CHART.lst, fontSize: 10 }} axisLine={false} tickLine={false} domain={["auto", "auto"]} />
                      <YAxis yAxisId="n" orientation="right" tick={{ fill: CHART.ndvi, fontSize: 10 }} axisLine={false} tickLine={false} domain={["auto", "auto"]} />
                      <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Area yAxisId="t" type="monotone" dataKey="temp" name="Temp (°C)" stroke={CHART.lst} fill="url(#gCorrTemp)" strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} activeDot={{ r: 4, strokeWidth: 0 }} />
                      <Area yAxisId="n" type="monotone" dataKey="ndvi" name="NDVI" stroke={CHART.ndvi} fill="url(#gCorrNdvi)" strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} activeDot={{ r: 4, strokeWidth: 0 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-[11px] text-[#475569] mt-2">
                  Temperatura (naranja, eje izq.) y NDVI del valle (verde, eje der.) por año.
                </p>
              </div>
            </div>

            <div className="border border-[#e5e7eb] bg-[#f8faf9] rounded-lg p-4 text-sm text-[#1f2937] leading-relaxed" data-testid="interpretation">
              <div className="plex font-medium mb-2 text-[#1a2318]">Interpretación</div>
              <p>
                La correlación entre la temperatura de superficie y el NDVI de las zonas agrícolas es <strong className="text-[#3f8e33]">{strength(rN?.r)}</strong> (r = {rN?.r}),
                calculada con LST y NDVI de Landsat a 30 m.
                {" "}Un valor negativo sugiere que los años más cálidos tienden a <em>asociarse</em> con menor vigor vegetal,
                mientras que uno positivo indica lo contrario. Se trata de una <strong>relación observada</strong>, no de una relación causal.
              </p>
              <p className="text-[12px] text-[#475569] mt-2">
                Nota: la correlación no implica causalidad; el NDVI también responde a precipitación, riego y manejo agrícola.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function RCard({ title, r, testId }) {
  const color = r == null ? "#94a3b8" : r < 0 ? CHART.navy : CHART.ndvi;
  return (
    <div className="border border-[#e5e7eb] bg-[#f8faf9] rounded-lg p-4" data-testid={testId}>
      <div className="label-caps">{title}</div>
      <div className="flex items-baseline gap-3 mt-1">
        <span className="mono text-4xl font-semibold" style={{ color }}>r = {r ?? "…"}</span>
      </div>
      <div className="text-xs text-[#334155] mt-1">Correlación {strength(r)}</div>
    </div>
  );
}
